// Settings → Connected email: connect / reconnect / disconnect Outlook, backfill progress,
// the "keep formatted copies" comparison switch, and the IT-admin approval flow.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { dateTime, relative } from '../lib/format';
import { download, getOutlookOpen, isWindows, removeCommand, setOutlookOpen, setupCommand, setupRegFile, type OutlookOpen } from '../lib/outlookOpen';
import { supabase, unwrap } from '../lib/supabase';
import { Button, Card, cx, Empty, ErrorBox, Pill, type PillColor } from './ui';

const STATUS: Record<string, { label: string; color: PillColor }> = {
  backfilling: { label: 'loading past email', color: 'blue' },
  active: { label: 'live', color: 'green' },
  needs_reconnect: { label: 'reconnect needed', color: 'red' },
  disconnected: { label: 'disconnected', color: 'gray' },
};

async function invoke<T>(name: string, body: object = {}): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    const ctx = (error as { context?: Response }).context;
    const msg = ctx ? ((await ctx.json().catch(() => null)) as { error?: string } | null)?.error : null;
    throw new Error(msg ?? error.message);
  }
  return data as T;
}

export function ConnectedEmail() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const result = params.get('mail');
  const detail = params.get('detail');
  const [adminLink, setAdminLink] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['mail-accounts'],
    queryFn: async () => {
      const [accounts, states] = await Promise.all([
        supabase.from('mail_accounts').select('id, email, status, store_html, backfill_months, backfill_done_at, connected_at, last_sync_at, last_error').order('connected_at'),
        supabase.from('mail_folder_state').select('account_id, folder, messages_seen, messages_matched, delta_link'),
      ]);
      return { accounts: unwrap(accounts), states: unwrap(states) };
    },
    refetchInterval: (q) => (q.state.data?.accounts.some((a) => a.status === 'backfilling') ? 10_000 : false),
  });

  const connect = useMutation({
    mutationFn: () => invoke<{ url: string; adminConsentUrl: string }>('mail-connect'),
    onSuccess: (r) => {
      window.location.href = r.url;
    },
  });
  const disconnect = useMutation({
    mutationFn: (accountId: string) => invoke('mail-disconnect', { account_id: accountId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail-accounts'] }),
  });

  // After Microsoft says an admin must approve: fetch the approval link to hand to IT.
  useEffect(() => {
    if (result === 'admin_needed' && !adminLink) invoke<{ adminConsentUrl: string }>('mail-connect').then((r) => setAdminLink(r.adminConsentUrl)).catch(() => {});
  }, [result, adminLink]);

  const clear = () => setParams({});
  const live = (data?.accounts ?? []).filter((a) => a.status !== 'disconnected');

  return (
    <Card
      title="Connected email"
      actions={
        <Button variant="primary" size="sm" busy={connect.isPending} onClick={() => connect.mutate()}>
          <Mail className="h-3.5 w-3.5" /> {live.length ? 'Connect another' : 'Connect Outlook'}
        </Button>
      }
    >
      <p className="mb-3 text-sm text-slate-600">
        Emails with your contacts appear on their timelines automatically (the last 6 months, then live). Emails with anyone else are never stored — frequent
        correspondents show up under Review as suggested contacts.
      </p>

      {result === 'connected' && <Notice color="green" onClose={clear}>Connected {detail}. Past email is loading in the background — it can take a while for a busy mailbox.</Notice>}
      {result === 'admin_approved' && <Notice color="green" onClose={clear}>Your IT admin approved SimpleFee. Click <b>Connect Outlook</b> now.</Notice>}
      {result === 'cancelled' && <Notice color="gray" onClose={clear}>Connection cancelled.</Notice>}
      {result === 'error' && (
        <Notice color="red" onClose={clear}>
          Couldn’t connect: {detail}
        </Notice>
      )}
      {result === 'admin_needed' && (
        <Notice color="amber" onClose={clear}>
          <p className="font-medium">Your company’s IT needs to approve SimpleFee once.</p>
          <p className="mt-1">
            Microsoft 365 companies usually block apps from reading mail until an admin approves them. Send your IT admin this link; after they approve, click
            Connect Outlook again.
          </p>
          {adminLink && (
            <div className="mt-2 space-y-2">
              <div className="flex gap-2">
                <input readOnly value={adminLink} className="w-full rounded border border-amber-300 bg-white px-2 py-1 text-xs" onFocus={(e) => e.target.select()} />
                <Button size="sm" onClick={() => navigator.clipboard.writeText(adminLink)}>
                  Copy
                </Button>
              </div>
              <Button
                size="sm"
                onClick={() =>
                  navigator.clipboard.writeText(
                    `Hi,\n\nCould you approve SimpleFee CRE for our Microsoft 365 account? It's the CRM I use; it needs read access to my mailbox (Mail.Read) to log emails with my clients, and permission to send my own morning summary email (Mail.Send). It only accesses the mailbox of the person who connects it.\n\nApproval link (needs a Global Admin, Cloud Application Admin or Application Admin):\n${adminLink}\n\nThanks!`,
                  )
                }
              >
                Copy an email to IT
              </Button>
            </div>
          )}
        </Notice>
      )}

      <ErrorBox error={connect.error ?? disconnect.error} />
      {isLoading ? null : live.length === 0 ? (
        <Empty>No mailbox connected yet.</Empty>
      ) : (
        <ul className="divide-y divide-slate-100">
          {live.map((a) => {
            const st = data!.states.filter((s) => s.account_id === a.id);
            const seen = st.reduce((t, s) => t + s.messages_seen, 0);
            const matched = st.reduce((t, s) => t + s.messages_matched, 0);
            const s = STATUS[a.status] ?? { label: a.status, color: 'gray' as PillColor };
            return (
              <li key={a.id} className="py-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-slate-900">{a.email}</span>
                  <Pill color={s.color}>{s.label}</Pill>
                  <span className="ml-auto flex gap-2">
                    {a.status === 'needs_reconnect' && (
                      <Button size="sm" variant="primary" onClick={() => connect.mutate()}>
                        Reconnect
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => confirm(`Stop syncing ${a.email}? Emails already on timelines stay.`) && disconnect.mutate(a.id)}>
                      Disconnect
                    </Button>
                  </span>
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {seen.toLocaleString()} emails checked · {matched.toLocaleString()} with your contacts
                  {a.backfill_done_at ? ` · history loaded ${dateTime(a.backfill_done_at)}` : ` · loading the last ${a.backfill_months} months…`}
                  {a.last_sync_at && ` · last sync ${relative(a.last_sync_at)}`}
                </div>
                {a.last_error && a.status !== 'needs_reconnect' && <div className="mt-1 text-xs text-amber-700">Last problem: {a.last_error}</div>}
              </li>
            );
          })}
        </ul>
      )}
      {live.length > 0 && <OpenInSetting />}
    </Card>
  );
}

/** Per device: where "Open in Outlook" on a timeline email goes. */
function OpenInSetting() {
  const [v, setV] = useState<OutlookOpen>(getOutlookOpen);
  const choose = (next: OutlookOpen) => (setOutlookOpen(next), setV(next));
  const windows = isWindows();
  const [copied, setCopied] = useState<'setup' | 'remove' | null>(null);
  const copy = (text: string, which: 'setup' | 'remove') =>
    navigator.clipboard.writeText(text).then(() => (setCopied(which), setTimeout(() => setCopied(null), 4000)));
  return (
    <div className="mt-3 border-t border-slate-100 pt-3 text-sm">
      <div className="font-medium text-slate-700">On this device, “Open in Outlook” opens…</div>
      <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1">
        <label className="flex items-center gap-2">
          <input type="radio" name="outlook-open" checked={v === 'web'} onChange={() => choose('web')} />
          Outlook on the web
        </label>
        <label className={cx('flex items-center gap-2', !windows && 'text-slate-400')}>
          <input type="radio" name="outlook-open" checked={v === 'desktop'} disabled={!windows && v !== 'desktop'} onChange={() => choose('desktop')} />
          Classic Outlook on this computer {!windows && '(Windows only)'}
        </label>
      </div>
      {v === 'desktop' && (
        <div className="mt-2 rounded-md bg-slate-50 p-3 text-xs text-slate-700">
          <div className="mb-1 font-medium">One-time setup on this computer (about a minute, no admin rights needed):</div>
          <ol className="list-decimal space-y-1 pl-4">
            <li>
              <Button size="sm" variant="primary" onClick={() => copy(setupCommand(), 'setup')}>
                {copied === 'setup' ? 'Copied ✓' : 'Copy setup command'}
              </Button>
            </li>
            <li>
              Press the <b>Windows key</b>, type <b>PowerShell</b> and open it (normally, not “as administrator”).
            </li>
            <li>
              Right-click in the PowerShell window to paste, then press <b>Enter</b>. It says “SimpleFee desktop links are set up.”
            </li>
            <li>Click “Open in Outlook” on any email. The first time, your browser asks to open SimpleFee CRE: tick “always allow” and click Open.</li>
          </ol>
          <p className="mt-2 text-slate-500">
            Classic Outlook needs to be installed and signed in to the same mailbox. If an email isn’t found, it may have been deleted; the “web” link next to it
            always works. This only changes settings for your own Windows user. To undo it, paste the{' '}
            <button className="text-indigo-600 hover:underline" onClick={() => copy(removeCommand(), 'remove')}>
              {copied === 'remove' ? 'remove command (copied ✓)' : 'remove command'}
            </button>{' '}
            the same way. Prefer a file? Use the{' '}
            <button className="text-indigo-600 hover:underline" onClick={() => download('SimpleFee-desktop-links.reg', setupRegFile())}>
              setup file
            </button>{' '}
            instead (Windows’ Smart App Control may block it); company IT can roll that file out to a whole team.
          </p>
        </div>
      )}
    </div>
  );
}

function Notice({ color, children, onClose }: { color: 'green' | 'amber' | 'red' | 'gray'; children: React.ReactNode; onClose: () => void }) {
  const styles = {
    green: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
    red: 'border-rose-200 bg-rose-50 text-rose-900',
    gray: 'border-slate-200 bg-slate-50 text-slate-700',
  }[color];
  return (
    <div className={`mb-3 rounded-md border px-3 py-2 text-sm ${styles}`}>
      <div className="flex justify-between gap-3">
        <div>{children}</div>
        <button className="text-xs opacity-60 hover:opacity-100" onClick={onClose}>
          dismiss
        </button>
      </div>
    </div>
  );
}
